import type { Pack } from "./pack";
import { ITEM_TYPES, type ItemOf, type ItemType } from "./schemas";

/**
 * An item paired with the id of the pack it came from. The Compendium tracks
 * provenance so the UI can show "from pack X" and so missing-content detection
 * can name the pack a referenced-but-absent id should have come from.
 */
export type OwnedItem<T extends ItemType> = {
  item: ItemOf<T>;
  packId: string;
}

/**
 * A merged, queryable view over one or more validated {@link Pack}s.
 *
 * Conflict rule — **later-pack-wins**: packs are merged in array order; when two
 * packs define the same `(type, id)`, the later pack's item replaces the
 * earlier one's. Rationale: the base pack (`@massif/lancer-data`) is loaded
 * first, so a user-supplied pack that intentionally overrides or errata-patches
 * a core item takes effect simply by being loaded after it — which matches
 * COMP/CON's "installed packs layer over base content" model and gives the user
 * a predictable, order-controlled override without namespacing every id (which
 * would break cross-pack references that use bare ids).
 */
export class Compendium {
  /** type -> id -> owned item (the winning entry after conflict resolution). */
  private readonly index: { [T in ItemType]: Map<string, OwnedItem<T>> };
  /** The packs merged into this compendium, in load order. */
  readonly packs: readonly Pack[];

  constructor(packs: Pack[] = []) {
    this.index = {} as { [T in ItemType]: Map<string, OwnedItem<T>> };
    for (const t of ITEM_TYPES) this.index[t] = new Map();
    this.packs = packs;
    for (const pack of packs) this.merge(pack);
  }

  /**
   * The per-type map, narrowed to its item type. `this.index[T]` is keyed by the
   * union `ItemType`, so TS widens a lookup at a specific `T`; this is the single
   * place that re-narrows it.
   */
  private typed<T extends ItemType>(type: T): Map<string, OwnedItem<T>> {
    return this.index[type] as Map<string, OwnedItem<T>>;
  }

  /** Merge one pack in (later-pack-wins for id collisions). Mutates this index. */
  private merge(pack: Pack): void {
    for (const type of ITEM_TYPES) {
      const map = this.typed(type);
      for (const item of pack.items[type]) {
        map.set(item.id, { item, packId: pack.id });
      }
    }
  }

  /** Resolve an item by type + id, or `undefined` if no loaded pack provides it. */
  getById<T extends ItemType>(type: T, id: string): ItemOf<T> | undefined {
    return this.typed(type).get(id)?.item;
  }

  /** Resolve an item *with its pack provenance*, or `undefined`. */
  getOwned<T extends ItemType>(type: T, id: string): OwnedItem<T> | undefined {
    return this.typed(type).get(id);
  }

  /** Whether the compendium can resolve `(type, id)`. */
  has(type: ItemType, id: string): boolean {
    return this.index[type].has(id);
  }

  /** All items of a type, after conflict resolution (insertion-ordered). */
  list<T extends ItemType>(type: T): ItemOf<T>[] {
    const out: ItemOf<T>[] = [];
    for (const owned of this.typed(type).values()) {
      out.push(owned.item);
    }
    return out;
  }

  /** Count of distinct items of a type after conflict resolution. */
  count(type: ItemType): number {
    return this.index[type].size;
  }

  /** Per-type counts, for diagnostics/validation. */
  counts(): Record<ItemType, number> {
    const out = {} as Record<ItemType, number>;
    for (const t of ITEM_TYPES) out[t] = this.count(t);
    return out;
  }
}
